import React from "react";
import PropTypes from "prop-types";
import { PlainText } from "@learningblocks/simulations/shared-components";
import { RectangleCard } from "@learningblocks/storybook-components";
import { NotebookImage } from "@learningblocks/simulations/shared-components";
import { HiddenComponent } from "@learningblocks/storybook-components";
import { StepSelectRedux } from "@learningblocks/storybook-components";
import { MultiSelectRedux } from "@learningblocks/storybook-components";
import { SingleSelectRedux } from "@learningblocks/simulations/shared-components";
import { SliderSelectRedux } from "@learningblocks/storybook-components";
import { Utilities } from "@learningblocks/exp-constants";
const { COMPONENT_TYPE } = Utilities;
import { SliderSelectGroupRedux } from "@learningblocks/storybook-components";
import { SingleSelectColorRedux } from "@learningblocks/storybook-components";
import { SliderImage } from "@learningblocks/storybook-components";
import { HighlightProvider } from "@learningblocks/shared-components";
import { SliderWidgetContainer } from "@learningblocks/storybook-components";
import { SummaryText } from "@learningblocks/simulations/shared-components";
import { TakeawayMessages } from "@learningblocks/simulations/shared-components";
import { BlottingPaper } from "@learningblocks/simulations/shared-components";
import { ImageTable } from "@learningblocks/simulations/shared-components";
import { TextArea } from "@learningblocks/simulations/shared-components";
import { MaterialSection } from "@learningblocks/simulations/shared-components";
import { ContextTable } from "@learningblocks/simulations/shared-components";
import { GelComponent } from "@learningblocks/protocol-components/lib/GelComponent";
import { RestrictionDigestPredictions } from "@learningblocks/protocol-components/lib/RestrictionDigestPredictions";
import { RestrictionDigestResults } from "@learningblocks/protocol-components/lib/RestrictionDigestResults";

/**
 * ContentComponents:
 *  - Display the contents of all Sections Except for DesignSection
 *    in Web View
 */
export function ContentComponents({ components, className }) {
  const { onPlainTextClick } = React.useContext(HighlightProvider.Context);

  const plainText = (component) => {
    const onClickHighlight = (contentId, event) => {
      onPlainTextClick(contentId, event);
    };
    return (
      <PlainText
        key={component.id}
        className={className}
        onClickHighlight={onClickHighlight}
        {...component}
      />
    );
  };

  const fullWidthImage = (component) => (
    <NotebookImage {...component} key={component.id} className={className} />
  );

  const sliderImage = (component) => {
    const [value, onChange] = React.useState(0);
    return (
      <SliderImage
        {...component}
        key={component.id}
        className={className}
        value={value}
        onChange={onChange}
      />
    );
  };

  const sliderWidget = (component) => {
    return (
      <SliderWidgetContainer
        {...component}
        key={component.id}
        className={className}
      />
    );
  };

  const singleSelect = (component) => (
    <SingleSelectRedux
      {...component}
      key={component.id}
      className={className}
    />
  );

  const multiSelect = (component) => (
    <MultiSelectRedux {...component} key={component.id} className={className} />
  );

  const stepSelect = (component) => {
    const options = component.options.map((option) => {
      return { value: option.id, label: option.text };
    });

    return (
      <StepSelectRedux
        {...component}
        key={component.id}
        className={className}
        options={options}
      />
    );
  };

  const sliderSelect = (component) => (
    <SliderSelectRedux
      {...component}
      key={component.id}
      className={className}
    />
  );
  const sliderSelectGroup = (component) => (
    <SliderSelectGroupRedux
      {...component}
      key={component.id}
      className={className}
    />
  );

  const singleSelectColorRedux = (component) => (
    <SingleSelectColorRedux
      {...component}
      key={component.id}
      className={className}
    />
  );

  const materialSection = (component) => {
    return <MaterialSection {...component} key={component.id} />;
  };

  const techniqueCard = (component) => {
    const MOBILE_WIDTH = 767;
    const currentScreenWidth = document.body.clientWidth;
    const trim = currentScreenWidth > MOBILE_WIDTH ? false : true;
    return (
      <RectangleCard
        {...component}
        key={component.id}
        className={className}
        trim={trim}
      />
    );
  };

  const summaryText = (component) => (
    <SummaryText key={component.id} {...component} />
  );

  const takeawayMessages = (component) => (
    <TakeawayMessages key={component.id} {...component} />
  );

  const blottingPaper = (component) => (
    <BlottingPaper key={component.id} {...component} />
  );

  const imageTable = (component) => (
    <ImageTable key={component.id} {...component} />
  );

  const textArea = (component) => (
    <TextArea key={component.id.toString()} {...component} />
  );

  const contextTable = (component) => (
    <ContextTable key={component.id} {...component} />
  );

  const gelComponent = (component) => (
    <GelComponent key={component.id} {...component} />
  );

  const restrictionDigestPredictions = (component) => (
    <RestrictionDigestPredictions key={component.id} {...component} />
  );

  const restrictionDigestResults = (component) => (
    <RestrictionDigestResults key={component.id} {...component} />
  );

  const renderComponent = (component) => {
    switch (component.type) {
      case COMPONENT_TYPE.PLAIN_TEXT:
        return plainText(component);
      case COMPONENT_TYPE.TEXT_AREA:
        return textArea(component);
      case COMPONENT_TYPE.TECHNIQUE_CARD:
        return techniqueCard(component);
      case COMPONENT_TYPE.NOTEBOOK_IMAGE:
        return fullWidthImage(component);
      case COMPONENT_TYPE.SLIDER_IMAGE:
        return sliderImage(component);
      case COMPONENT_TYPE.SLIDER_WIDGET:
        return sliderWidget(component);
      case COMPONENT_TYPE.SUMMARY_TEXT:
        return summaryText(component);
      case COMPONENT_TYPE.TAKEAWAY_MESSAGES:
        return takeawayMessages(component);
      case COMPONENT_TYPE.MATERIAL_SECTION:
        return materialSection(component);
      case COMPONENT_TYPE.BLOTTING_PAPER:
        return blottingPaper(component);
      case COMPONENT_TYPE.IMAGE_TABLE:
        return imageTable(component);
      case COMPONENT_TYPE.CONTEXT_TABLE:
        return contextTable(component);
      case COMPONENT_TYPE.GEL_COMPONENT:
        return gelComponent(component);
      case COMPONENT_TYPE.RESTRICTION_DIGEST_PREDICTIONS:
        return restrictionDigestPredictions(component);
      case COMPONENT_TYPE.RESTRICTION_DIGEST_RESULTS:
        return restrictionDigestResults(component);
      case COMPONENT_TYPE.CSCQ:
        return singleSelect(component);
      case COMPONENT_TYPE.MSCQ:
        return multiSelect(component);
      case COMPONENT_TYPE.DDQ:
        return stepSelect(component);
      case COMPONENT_TYPE.GROUPED_DNDQ:
        return (
          <HiddenComponent
            visibility={component.visibility}
            key={component.id}
            className={className}
          >
            {sliderSelectGroup(component)}
          </HiddenComponent>
        );
      case COMPONENT_TYPE.DNDQ_COLOR:
        return (
          <HiddenComponent
            visibility={component.visibility}
            key={component.id}
            className={className}
          >
            {sliderSelect(component)}
          </HiddenComponent>
        );
      case COMPONENT_TYPE.CSCQ_COLOR:
        return (
          <HiddenComponent
            visibility={component.visibility}
            key={component.id}
            className={className}
          >
            {singleSelectColorRedux(component)}
          </HiddenComponent>
        );
      default:
        return <h3> Component Type {component.type}</h3>;
    }
  };

  let componentList = [];

  for (let index in components) {
    let component = components[index];
    componentList.push(renderComponent(component));
    if (component && component.finalPoint) {
      break;
    }
  }

  return componentList;
}

ContentComponents.propTypes = {
  /**
   * Array of data corresponding to component to be
   * displayed in content
   */
  components: PropTypes.arrayOf(PropTypes.object),
  /**
   * ClassName to set on each component in List
   */
  className: PropTypes.string,
};

ContentComponents.defaultProps = {};
